import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { InputRef } from "antd";
import { Input, Tag, Tooltip } from "antd";
import { TAGS_MODULE_MAPPING } from "@ts/constants";
import { showToast } from "@ts/modals";
import { FaTags, FaTimes } from "react-icons/fa";
import { getTagType } from "@ts/utils";
import { TagsMap } from "@/ts/common-types";
import usePermissionCheck from "@/hooks/usePermissionCheck";

interface IDynamicTagsComponent {
  isEditable: boolean;
  tags: string[];
  setTags: Dispatch<SetStateAction<string[]>>;
  avlTagsConstant?: string[];
  setAvlTags?: Dispatch<SetStateAction<string[]>>;
  inputValue?: string;
  setInputValue?: Dispatch<SetStateAction<string>>;
  activeModule: string

}

const DynamicTagsComponent: React.FC<IDynamicTagsComponent> = ({
  isEditable,
  tags,
  setTags,
  avlTagsConstant,
  setAvlTags,
  inputValue,
  setInputValue,
    activeModule
}) => {
  const [inputVisible, setInputVisible] = useState(false);
  const inputRef = useRef<InputRef>(null);
  const { isAddTagsAllowed, recordType } =
    TAGS_MODULE_MAPPING[activeModule as keyof TagsMap];

  useEffect(() => {
    if (inputVisible) {
      inputRef.current?.focus();
    }
  }, [inputVisible]);

  const handleClose = (removedTag: string) => {
    setInputValue("");
    const [tagId] = removedTag.split("##");
    const newTags = tags.filter((tag: string) => tag.split("##")[0] !== tagId);
    setTags(newTags);
    setAvlTags((prev: string[]) => [...prev, removedTag]);
  };

  const showInput = () => {
    if (isEditable) {
      setInputVisible(true);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (tags.length <= 10) {
      setInputValue(e.target.value);
    } else {
      showToast("danger", "Max tags limit reached.");
    }
  };

  const handleInputConfirm = () => {
    if (
      inputValue &&
      avlTagsConstant.includes(inputValue) &&
      !tags.includes(inputValue)
    ) {
      setTags([...tags, inputValue]);
      setInputValue("");
    }
  };

  const editTagCommonStyle = {
    height: 22,
    borderStyle: "dashed",
  };

  const { canDeleteTags } = usePermissionCheck();

  return (
    <div
      className={`${!isEditable ? "" : "dynamic-tags-container "}`}
      onClick={showInput}
    >
      {tags.map((tag: string, index: number) => {
        const tagValue = tag.split("##")[1];
        const { tagType, tagFillColor, tagBorderColor } = getTagType(tag, recordType);
        const isLongTag = tagValue?.length > 20;
        const tagElem = (
          <Tag
            key={`${tagType}-${tagValue}`}
            closable={isEditable}
            onClose={() => !canDeleteTags ? null : handleClose(tag)}
            closeIcon={
              !canDeleteTags ? "" : < FaTimes
                style={{
                  fontSize: "12px",
                  marginBottom: 1,
                  marginRight: 5,
                }}
              />
            }
            className={`m-1 ${isEditable ? "shadow-sm" : ""}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              borderRadius: "4px",
              border: `1px solid ${tagBorderColor}`,
              overflow: "hidden",
              textOverflow: "ellipsis",
              padding: 0,
              ...(!isEditable && {
                maxWidth: 160,
              }),
            }}
          >
            <span
              style={{
                width: 25,
                justifyContent: "center",
                backgroundColor: `${tagFillColor}`,
                color: `white`,
                padding: "0 8px",
                display: "flex",
                alignItems: "center",
                borderRight: `1px solid ${tagBorderColor}`,
                flexShrink: 0,
              }}
            >
              {tagType[0]}
            </span>
            <span
              style={{
                paddingLeft: "8px",
                ...(!isEditable && {
                  paddingRight: "8px",
                }),
                color: `black`,
                overflow: "hidden",
                textOverflow: "ellipsis",
                flexGrow: 1,
              }}
            >
              {tagValue}
            </span>
          </Tag>
        );
        return isLongTag ? (
          <Tooltip title={tagValue} key={tagValue}>
            {tagElem}
          </Tooltip>
        ) : (
          tagElem
        );
      })}
      {
        inputVisible || (isEditable && !tags.length) ? (
          <Input
            ref={inputRef}
            type="text"
            size="small"
            style={{
              width: "100%",
              height: 22,
              marginInlineEnd: 8,
              verticalAlign: "top",
              background: "transparent",
            }}
            placeholder="Select Tags"
            bordered={false}
            value={inputValue}
            onChange={handleInputChange}
            onPressEnter={handleInputConfirm}
          />
        ) : (
          isAddTagsAllowed && (
            <Tag
              className="btn btn-sm mt-2 bg-white text-dark border border-sm rounded-3"
              style={
                isEditable
                  ? tags.length
                    ? { display: "none" }
                    : { ...editTagCommonStyle, opacity: 0 }
                  : tags.length
                    ? { display: "none" }
                    : {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "fit-content",
                      margin: "0 auto"
                    }
              }
              icon={<FaTags className="me-1" />}
              onClick={showInput}
            >
              Select Tags
            </Tag>
          )
        )
      }
    </div >
  );
};

export default DynamicTagsComponent;